import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiX, FiLayout, FiPlus, FiTrash2, FiCheck } = FiIcons;

const LeadFormModal = ({ isOpen, onClose, form }) => {
  const { addLeadForm, updateLeadForm, customFields } = useData();
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    button_text: 'Submit Request',
    success_message: 'Thanks! We will contact you soon.',
    fields: [
      { id: 'name', label: 'Full Name', required: true, type: 'text' },
      { id: 'email', label: 'Email Address', required: true, type: 'email' }
    ]
  });

  useEffect(() => {
    if (form) setFormData(form);
    else setFormData({
      name: '',
      title: '',
      description: '',
      button_text: 'Submit Request',
      success_message: 'Thanks! We will contact you soon.',
      fields: [
        { id: 'name', label: 'Full Name', required: true, type: 'text' },
        { id: 'email', label: 'Email Address', required: true, type: 'email' }
      ]
    });
  }, [form, isOpen]);

  const addField = (fieldId, label, type) => {
    if (formData.fields.find(f => f.id === fieldId)) return;
    setFormData({
      ...formData,
      fields: [...formData.fields, { id: fieldId, label, required: false, type }]
    });
  };

  const removeField = (id) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter(f => f.id !== id)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form) await updateLeadForm(form.id, formData);
    else await addLeadForm(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden"
        >
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiLayout} className="text-blue-600" />
              {form ? 'Edit Lead Form' : 'New Lead Form'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Internal Form Name</label>
                <input 
                  required
                  className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" 
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Public Title</label>
                <input 
                  className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" 
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Submit Button Text</label>
                <input 
                  className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" 
                  value={formData.button_text}
                  onChange={e => setFormData({...formData, button_text: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Active Form Fields</h3>
              <div className="space-y-2">
                {formData.fields.map(field => (
                  <div key={field.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-blue-600 font-black text-[10px]">
                        {field.type.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-bold text-slate-700">{field.label}</span>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => removeField(field.id)}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                ))}
              </div>

              <div className="pt-4 space-y-2">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Add Available Fields</p>
                <div className="flex flex-wrap gap-2">
                  {['phone', 'company', 'notes'].map(f => (
                    <button 
                      key={f}
                      type="button"
                      onClick={() => addField(f, f.charAt(0).toUpperCase() + f.slice(1), 'text')}
                      className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-500 hover:border-blue-600 hover:text-blue-600 transition-all"
                    >
                      + {f}
                    </button>
                  ))}
                  {customFields.map(cf => (
                    <button 
                      key={cf.id}
                      type="button"
                      onClick={() => addField(`custom_${cf.id}`, cf.label, cf.field_type)}
                      className="px-4 py-2 bg-blue-50 border border-blue-100 rounded-xl text-xs font-black text-blue-600 hover:bg-blue-600 hover:text-white transition-all"
                    >
                      + {cf.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all">
              {form ? 'Update Form' : 'Generate Form'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LeadFormModal;